import { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDisaggregationList,
  formatCountriesForQueryParams,
  formatFilterForQueryParams,
  getCustomDimensionName,
  getCustomDimensionValue,
  getUnitMeasureItems,
} from "../common/utils/helper";
import {
  setInitialFilters,
  setSelectedComparison,
  setSelectedCountries,
  setSelectedCountriesForBar,
  setSelectedFilters,
  seIsSelectedFiltersUpdated,
  setSelectedUnitMeasure,
  setSelectedFrequency,
  setUnitMeasureName,
} from "./indicatorProfileSlice";
import {
  EXCLUDED_DISAGGREGATIONS,
  NONE,
  TOTAL,
} from "../common/utils/constants";
import { QueryParams, chartTypes, disaggregations } from "../common/utils/enum";
import useQueryParams from "../common/hooks/useQueryParams";
import useCloseOnOutsideClick from "../common/hooks/useCloseOnOutsideClick";

const ComparisonByDisaggregation = ({ chartType, callDimensionsAPI }) => {
  const [comparisonByData, setComparisonByData] = useState([]);
  const [filterTooltips, setFilterTooltips] = useState({});
  const [activeTooltip, setActiveTooltip] = useState(null);

  const {
    selectedComparison,
    selectedCountries,
    selectedCountriesForBar,
    selectedFilters,
    disaggregationData,
    dimensionsData,
    changedFilterName,
    selectedUnitMeasure,
    selectedFrequency,
    isDimensionsLoading,
    selectedDimensionsList,
  } = useSelector((state) => state.indicatorProfile);
  const { featureProps } = useSelector((state) => state.common);
  const { uhcTooltipsJSONPath } = featureProps || {};

  useEffect(() => {
    const fetchFilterTooltips = async () => {
      try {
        const response = await fetch(uhcTooltipsJSONPath);
        if (response.ok) {
          const data = await response.json();
          setFilterTooltips(data);
        }
      } catch (error) {
        console.error("Error fetching filter tooltips:", error);
      }
    };

    if (uhcTooltipsJSONPath) {
      fetchFilterTooltips();
    }
  }, [uhcTooltipsJSONPath]);

  const getFilterTooltip = (filterName, valueId) => {
    if (filterTooltips[filterName] && filterTooltips[filterName][valueId]) {
      return filterTooltips[filterName][valueId];
    }
    return null;
  };

  const tooltipStyles = {
    position: "absolute",
  };

  const dispatch = useDispatch();
  const { setQueryParam, deleteQueryParams, getQueryParam } = useQueryParams();
  const custDimMultiSelectListRef = useRef(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [selectAllChecked, setSelectAllChecked] = useState(true);
  const [currentRadioSelections, setCurrentRadioSelections] = useState({});

  // To close multiselect cust dimenstion list on outside click
  const closeCustDimMultiSelectList = (e) => {
    if (!e.target.dataset.multiCheckbox) setActiveDropdown(null);
  };

  // To close multiselect cust dimenstion list on outside click
  useCloseOnOutsideClick(custDimMultiSelectListRef, (e) =>
    closeCustDimMultiSelectList(e),
  );

  // To toggle multi select cust dimension list open/close
  const toggleCheckboxGroup = (dropdownKey) => {
    setActiveDropdown((prev) => (prev === dropdownKey ? null : dropdownKey));
  };

  // Close tooltip on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (activeTooltip && !e.target.closest(".tui__filter_info_icon")) {
        setActiveTooltip(null);
      }
    };
    if (activeTooltip) {
      document.addEventListener("click", handleOutsideClick);
      return () => {
        document.removeEventListener("click", handleOutsideClick);
      };
    }
  }, [activeTooltip]);

  useEffect(() => {
    if (disaggregationData?.dimensions?.length > 0) {
      const finalDisaggregationList = fetchDisaggregationList(
        disaggregationData?.dimensions,
      ).filter((item) => item.field_value && item.field_value.length > 1);
      setComparisonByData(finalDisaggregationList);
      initializeFilters(true);
    }
  }, [disaggregationData]);

  const initializeFilters = (onLoad) => {
    let hasFilterQueryParams = false;
    
    const disaggregationList = fetchDisaggregationList(
      disaggregationData?.dimensions,
    );
    const comparisonByFromUrl = getQueryParam(QueryParams.ComparisonBy);
    if (onLoad) {
      for (let i = 0; i < disaggregationList.length; i++) {
        const queryParamsFilterValue = getQueryParam(
          disaggregationList[i].field_name,
        );
        // selected comparison is sex and sex has empty query param means none of checkboxes selected.
        // TO-DO: test when an indicator is available with multiple disaggregations
        if (queryParamsFilterValue || queryParamsFilterValue === "") {
          hasFilterQueryParams = true;
          break;
        }
      }
    }
    if (hasFilterQueryParams) {
      let filters = {};
      disaggregationList.forEach((filter) => {
        filters[filter.field_name] = getQueryParam(filter.field_name);
      });
      handleFilterChange({ target: { name: "init", value: filters } });
    } else {
      const initialFilters = {};
      disaggregationList.forEach((filter) => {
        if (filter.field_value?.length > 0) {
          if (
            comparisonByFromUrl &&
            comparisonByFromUrl === filter.field_name
          ) {
            const ids = filter.field_value
              ?.map((v) => v.code)
              .slice(0, 6)
              .join(",");
            initialFilters[filter.field_name] = ids;
          } else {
            const defaultOption = filter.field_value[0];
            initialFilters[filter.field_name] = defaultOption.code;
          }
        }
      });
      if (!onLoad) {
        // when comparison by selected None then deleting queryparams since the filters will be changed to default when None is selected
        // TO-DO: need to update when new disaggregations are introduced
        const queryParamsToDelete = disaggregationData?.dimensions
          ?.map((item) => item.field_name)
          .filter((item) => !EXCLUDED_DISAGGREGATIONS.includes(item));
        deleteQueryParams(queryParamsToDelete);
      }
      dispatch(setInitialFilters(initialFilters));
      dispatch(seIsSelectedFiltersUpdated(true));
    }
  };

  const validateAndUpdateUnitMeasureAndFrequency = (dimensions) => {
    if (dimensions?.[disaggregations.UnitMeasure] && selectedUnitMeasure) {
      if (
        !dimensions?.[disaggregations.UnitMeasure].includes(selectedUnitMeasure)
      ) {
        const newUnitMeasure =
          dimensions?.[disaggregations.UnitMeasure][0] || selectedUnitMeasure;
        dispatch(setSelectedUnitMeasure(newUnitMeasure));
        setQueryParam(QueryParams.UnitMeasure, newUnitMeasure);

        const unitMeasureItem = getUnitMeasureItems(disaggregationData)?.find(
          (item) => item.code === newUnitMeasure,
        );
        const unitMeasureName =
          unitMeasureItem?.description || unitMeasureItem?.code;
        dispatch(setUnitMeasureName(unitMeasureName));
      }
    }

    if (dimensions?.[disaggregations.Frequency] && selectedFrequency) {
      if (
        !dimensions?.[disaggregations.Frequency].includes(selectedFrequency)
      ) {
        const newFrequency =
          dimensions?.[disaggregations.Frequency][0] || selectedFrequency;
        dispatch(setSelectedFrequency(newFrequency));
        setQueryParam(QueryParams.Frequency, newFrequency);
      }
    }
  };

  const validateAndUpdateFilters = (
    dimensions,
    updatedFilters,
    currentFilterName,
  ) => {
    Object.keys(updatedFilters).forEach((key) => {
      if (key === currentFilterName) return;

      const availableValues = dimensions[key];
      const currentValue = updatedFilters[key];

      if (!availableValues || !currentValue) return;

      const currentValues = currentValue.split(",");
      const validValues = currentValues.filter((val) =>
        availableValues.includes(val),
      );

      if (validValues.length === 0 && availableValues.length > 0) {
        updatedFilters[key] = availableValues[0];
      } else {
        updatedFilters[key] = validValues.join(",");
      }
    });

    return updatedFilters;
  };

  const handleComparisonByChange = (event) => {
    const comparisonBy = event.target.value;
    dispatch(setSelectedComparison(comparisonBy));
    // setQueryParam(QueryParams.ComparisonBy, event.target.value)
    if (comparisonBy !== NONE) {
      setQueryParam(QueryParams.ComparisonBy, comparisonBy);
    } else {
      deleteQueryParams([QueryParams.ComparisonBy]);
    }

    let shouldUpdateDimensionsList = false;
    let updatedDimensionsListForAPI = [...(selectedDimensionsList || [])];

    if (
      comparisonBy !== NONE &&
      chartType === chartTypes.Trend &&
      selectedCountries.length > 0
    ) {
      const countriesToRemove = selectedCountries?.slice(1);

      setQueryParam(
        QueryParams.CountrySelection,
        formatCountriesForQueryParams([selectedCountries[0]]),
      );
      dispatch(setSelectedCountries([selectedCountries[0]]));
      if (countriesToRemove?.length > 0) {
        shouldUpdateDimensionsList = true;
        updatedDimensionsListForAPI = updatedDimensionsListForAPI
          ?.map((item) => {
            if (item.filterColumn === disaggregations.RefArea) {
              const currentValues = item?.filterValue?.split(",");
              const remainingValues = currentValues?.filter(
                (val) => !countriesToRemove?.includes(val),
              );
              return remainingValues?.length > 0
                ? { ...item, filterValue: remainingValues?.join(",") }
                : null;
            }
            return item;
          })
          ?.filter((item) => item !== null);
      }
      if (selectedCountriesForBar.includes("all")) {
        let countries = selectedCountriesForBar.filter(
          (item) => item === "all",
        );
        countries.push(selectedCountries[0]);
        dispatch(setSelectedCountriesForBar(countries));
      } else {
        dispatch(setSelectedCountriesForBar([selectedCountries[0]]));
      }
    } else if (
      comparisonBy !== NONE &&
      chartType === chartTypes.Bar &&
      selectedCountriesForBar.length > 0
    ) {
      setQueryParam(
        QueryParams.CountrySelection,
        formatCountriesForQueryParams(selectedCountriesForBar),
      );
      let countries = [];
      if (
        selectedCountriesForBar.includes("all") &&
        selectedCountriesForBar.filter((item) => item !== "all").length > 0
      ) {
        countries = selectedCountriesForBar.filter((item) => item !== "all");
      } else if (!selectedCountriesForBar.includes("all")) {
        countries = selectedCountriesForBar;
      }
      // set countries for other charts excluding all
      dispatch(setSelectedCountries(countries));

      // if (selectedCountriesForBar.includes('all')) {
      //   dispatch(setSelectedCountriesForBar(selectedCountriesForBar))
      // } else {
      //   dispatch(setSelectedCountriesForBar([selectedCountriesForBar[0]]))
      // }
      // allow multiple selections in comparison by other than none for bar
      dispatch(setSelectedCountriesForBar(selectedCountriesForBar));
      if (countries?.length > 0) {
        const allCountries = selectedCountriesForBar?.filter(
          (item) => item !== "all",
        );
        const countriesToRemove = allCountries?.filter(
          (country) => !countries?.includes(country),
        );

        if (countriesToRemove?.length > 0) {
          shouldUpdateDimensionsList = true;
          updatedDimensionsListForAPI = updatedDimensionsListForAPI
            ?.map((item) => {
              if (item.filterColumn === disaggregations.RefArea) {
                const currentValues = item?.filterValue?.split(",");
                const remainingValues = currentValues?.filter(
                  (val) => !countriesToRemove?.includes(val),
                );
                return remainingValues?.length > 0
                  ? { ...item, filterValue: remainingValues?.join(",") }
                  : null;
              }
              return item;
            })
            ?.filter((item) => item !== null);
        }
      }
    }
    if (comparisonBy === NONE) {
      initializeFilters(false);

      if (selectedComparison && selectedComparison !== NONE) {
        const previousFilter = fetchDisaggregationList(
          disaggregationData?.dimensions,
        ).find((filter) => filter.field_name === selectedComparison);
        if (previousFilter) {
          const defaultOption = previousFilter.field_value[0];
          const disaggFilterName = selectedComparison;

          callDimensionsAPI(
            disaggFilterName,
            defaultOption.code,
            shouldUpdateDimensionsList ? updatedDimensionsListForAPI : null,
          );
        }
      }
    } else {
      const updatedFilters = {};
      fetchDisaggregationList(disaggregationData?.dimensions).forEach(
        (filter) => {
          if (filter.field_name === comparisonBy) {
            const ids = filter.field_value
              ?.map((v) => v.code)
              .slice(0, 6)
              .join(",");

            updatedFilters[filter.field_name] = ids;
          } else {
            const defaultOption = filter.field_value[0];
            updatedFilters[filter.field_name] = defaultOption.code;
          }
        },
      );
      handleFilterChange({ target: { name: "init", value: updatedFilters } });

      if (comparisonBy !== NONE) {
        const selectedFilter = Object.keys(updatedFilters).find(
          (key) => key === comparisonBy,
        );
        if (selectedFilter) {
          const disaggFilterName = selectedFilter;
          callDimensionsAPI(
            disaggFilterName,
            updatedFilters[selectedFilter],
            shouldUpdateDimensionsList ? updatedDimensionsListForAPI : null,
          );
        }
      }
    }
  };

  const handleDropdownCheckboxChange = async (event, filterName) => {
    const { value, checked } = event.target;
    let newValues = selectedFilters[filterName]
      ? selectedFilters[filterName].split(",")
      : [];
    if (checked) {
      newValues.push(value);
    } else {
      newValues = newValues.filter((val) => val !== value);
    }
    // Handle Select All Conditions
    if (newValues.length === dimensionValues.length) {
      setSelectAllChecked(true);
    } else {
      setSelectAllChecked(false);
    }

    setCurrentRadioSelections((prev) => ({
      ...prev,
      [filterName]: newValues.join(","),
    }));

    try {
      const dimensions = await callDimensionsAPI(
        filterName,
        newValues.join(","),
      );

      if (dimensions) {
        const updatedFilters = {
          ...selectedFilters,
          [filterName]: newValues.join(","),
        };

        validateAndUpdateFilters(dimensions, updatedFilters, filterName);

        validateAndUpdateUnitMeasureAndFrequency(dimensions);

        const filters = formatFilterForQueryParams({
          name: filterName,
          value: updatedFilters[filterName],
          selectedComparison,
          selectedFilters: updatedFilters,
        });
        for (const [key, queryValue] of Object.entries(filters)) {
          setQueryParam(key, queryValue);
        }
        dispatch(
          setSelectedFilters({
            name: "init",
            value: updatedFilters,
            selectedComparison,
          }),
        );
      } else {
        const filters = formatFilterForQueryParams({
          name: filterName,
          value: newValues.join(","),
          selectedComparison,
          selectedFilters,
        });
        for (const [key, value] of Object.entries(filters)) {
          setQueryParam(key, value);
        }
        dispatch(
          setSelectedFilters({
            name: filterName,
            value: newValues.join(","),
            selectedComparison,
          }),
        );
      }
    } finally {
      setCurrentRadioSelections((prev) => {
        if (prev[filterName] !== newValues.join(",")) {
          return prev;
        }
        const next = { ...prev };
        delete next[filterName];
        return next;
      });
    }
  };

  const handleCheckboxChange = async (event, filterName) => {
    const { value, checked } = event.target;
    let newValues = selectedFilters[filterName]
      ? selectedFilters[filterName].split(",")
      : [];
    if (checked) {
      newValues.push(value);
    } else {
      newValues = newValues.filter((val) => val !== value);
    }

    setCurrentRadioSelections((prev) => ({
      ...prev,
      [filterName]: newValues.join(","),
    }));

    try {
      const dimensions = await callDimensionsAPI(
        filterName,
        newValues.join(","),
      );

      if (dimensions) {
        const updatedFilters = {
          ...selectedFilters,
          [filterName]: newValues.join(","),
        };

        validateAndUpdateFilters(dimensions, updatedFilters, filterName);

        validateAndUpdateUnitMeasureAndFrequency(dimensions);

        const filters = formatFilterForQueryParams({
          name: filterName,
          value: updatedFilters[filterName],
          selectedComparison,
          selectedFilters: updatedFilters,
        });
        for (const [key, queryValue] of Object.entries(filters)) {
          setQueryParam(key, queryValue);
        }
        dispatch(
          setSelectedFilters({
            name: "init",
            value: updatedFilters,
            selectedComparison,
          }),
        );
      } else {
        const filters = formatFilterForQueryParams({
          name: filterName,
          value: newValues.join(","),
          selectedComparison,
          selectedFilters,
        });
        for (const [key, value] of Object.entries(filters)) {
          setQueryParam(key, value);
        }
        dispatch(
          setSelectedFilters({
            name: filterName,
            value: newValues.join(","),
            selectedComparison,
          }),
        );
      }
    } finally {
      setCurrentRadioSelections((prev) => {
        if (prev[filterName] !== newValues.join(",")) {
          return prev;
        }
        const next = { ...prev };
        delete next[filterName];
        return next;
      });
    }
  };

  const handleFilterChange = async (event) => {
    const { name, value } = event.target;
    if (name === "init") {
      dispatch(setSelectedFilters({ name, value, selectedComparison }));
      return;
    }

    setCurrentRadioSelections((prev) => ({
      ...prev,
      [name]: value,
    }));

    try {
      const dimensions = await callDimensionsAPI(name, value);
      if (dimensions) {
        const updatedFilters = { ...selectedFilters, [name]: value };

        validateAndUpdateFilters(dimensions, updatedFilters, name);

        validateAndUpdateUnitMeasureAndFrequency(dimensions);

        const filters = formatFilterForQueryParams({
          name,
          value: updatedFilters[name],
          selectedComparison,
          selectedFilters: updatedFilters,
        });

        for (const [key, queryValue] of Object.entries(filters)) {
          setQueryParam(key, queryValue);
        }
        dispatch(
          setSelectedFilters({
            name: "init",
            value: updatedFilters,
            selectedComparison,
          }),
        );
      } else {
        const filters = formatFilterForQueryParams({
          name,
          value,
          selectedComparison,
          selectedFilters,
        });
        for (const [key, queryValue] of Object.entries(filters)) {
          setQueryParam(key, queryValue);
        }
        dispatch(setSelectedFilters({ name, value, selectedComparison }));
      }
    } finally {
      setCurrentRadioSelections((prev) => {
        if (prev[name] !== value) {
          return prev;
        }
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSelectAllChange = async (e, filterName) => {
    const newSelectAllChecked = !selectAllChecked;
    setSelectAllChecked(newSelectAllChecked);
    let newValues = [];

    if (newSelectAllChecked) {
      newValues = dimensionValues.map((val) => val.code);
    }

    const dimensions = await callDimensionsAPI(filterName, newValues.join(","));

    if (dimensions) {
      const updatedFilters = {
        ...selectedFilters,
        [filterName]: newValues.join(","),
      };

      validateAndUpdateFilters(dimensions, updatedFilters, filterName);

      validateAndUpdateUnitMeasureAndFrequency(dimensions);

      const filters = formatFilterForQueryParams({
        name: filterName,
        value: updatedFilters[filterName],
        selectedComparison,
        selectedFilters: updatedFilters,
      });
      for (const [key, queryValue] of Object.entries(filters)) {
        setQueryParam(key, queryValue);
      }
      dispatch(
        setSelectedFilters({
          name: "init",
          value: updatedFilters,
          selectedComparison,
        }),
      );
    } else {
      const filters = formatFilterForQueryParams({
        name: filterName,
        value: newValues.join(","),
        selectedComparison,
        selectedFilters,
      });
      for (const [key, value] of Object.entries(filters)) {
        setQueryParam(key, value);
      }
      dispatch(
        setSelectedFilters({
          name: filterName,
          value: newValues.join(","),
          selectedComparison,
        }),
      );
    }
  };

  const dimensionValues =
    comparisonByData.find((filter) => filter.field_name === selectedComparison)
      ?.field_value || [];
  return (
    <>
      {comparisonByData.length > 0 && (
        <div className="tui__filter_input_group">
          {chartType !== chartTypes.Correlation &&
            chartType !== chartTypes.DataTable &&
            chartType !== chartTypes.Map && (
              <fieldset className="primary_radio" id="compare_fieldset_overall">
                <legend id="comparison">Comparison by</legend>
                <div className="tui__radio_group">
                  <div className="tui__radio">
                    <input
                      className="tui__primary_radio"
                      type="radio"
                      id="none"
                      name="compare"
                      value={NONE}
                      checked={selectedComparison === NONE}
                      onChange={handleComparisonByChange}
                    />
                    <label htmlFor="none">
                      <span>{NONE}</span>
                    </label>
                  </div>
                  {comparisonByData.length > 0 &&
                    comparisonByData.map((filter) => (
                      <div className="tui__radio" key={filter.field_name}>
                        <input
                          className="tui__primary_radio"
                          type="radio"
                          id={filter.field_name}
                          name="compare"
                          value={filter.field_name}
                          checked={selectedComparison === filter.field_name}
                          onChange={handleComparisonByChange}
                        />
                        <label htmlFor={filter.field_name}>
                          <span>
                            {filter.field_name?.startsWith("COMP_BREAKDOWN")
                              ? getCustomDimensionName(
                                  filter.field_value,
                                  filter.field_name,
                                )
                              : filter?.label_name?.trim()
                                ? filter.label_name
                                : ""}
                          </span>
                        </label>
                      </div>
                    ))}
                </div>
                {chartType !== chartTypes.StackedBar &&
                  chartType !== chartTypes.Pie &&
                  chartType !== chartTypes.Bar && (
                    <p className="tui_text_sm">
                      Select “None” to display multiple countries.
                    </p>
                  )}
              </fieldset>
            )}

          {selectedComparison === NONE &&
            chartType !== chartTypes.DataTable &&
            comparisonByData.map((filter) =>
              filter.field_value?.length <= 6 ? (
                <fieldset
                  key={filter.field_name}
                  className="primary_radio"
                  id={`compare_fieldset_${filter.field_name}`}
                >
                  <legend id={filter.field_name}>
                    {filter.field_name?.startsWith("COMP_BREAKDOWN")
                      ? getCustomDimensionName(
                          filter.field_value,
                          filter.field_name,
                        )
                      : filter?.label_name?.trim()
                        ? filter.label_name
                        : ""}
                  </legend>
                  <div className="tui__radio_group">
                    {filter.field_value.map((value) => {
                      const pendingValue =
                        currentRadioSelections[filter.field_name];
                      const selectedValue = selectedFilters[filter.field_name];
                      const activeValue =
                        pendingValue !== undefined
                          ? isDimensionsLoading
                            ? pendingValue
                            : (selectedValue ?? pendingValue)
                          : selectedValue;

                      return (
                        <div
                          key={`${filter.field_name}_${value.code}`}
                          className="tui__radio"
                        >
                          <input
                            className="tui__primary_radio"
                            type="radio"
                            id={`${filter.field_name}_${value.code}`}
                            name={filter.field_name}
                            value={value.code}
                            checked={activeValue === value.code}
                            onChange={handleFilterChange}
                            disabled={
                              dimensionsData &&
                              changedFilterName !== filter.field_name &&
                              !dimensionsData[filter.field_name]?.includes(
                                value.code,
                              )
                            }
                          />
                          <label htmlFor={`${filter.field_name}_${value.code}`}>
                            <span>
                              {filter.field_name?.startsWith("COMP_BREAKDOWN")
                                ? getCustomDimensionValue(value)
                                : value.description || value.code}
                            </span>
                          </label>
                          {getFilterTooltip(filter.field_name, value.code) && (
                            <div
                              className="filter-tooltip"
                              style={{
                                position: "relative",
                                display: "inline-block",
                                marginLeft: "8px",
                              }}
                            >
                              <span
                                className="tui__filter_info_icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveTooltip(
                                    activeTooltip ===
                                      `tooltip_${filter.field_name}_${value.code}`
                                      ? null
                                      : `tooltip_${filter.field_name}_${value.code}`,
                                  );
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                <i className="lp lp-info-circle-solid"></i>
                              </span>
                              {activeTooltip ===
                                `tooltip_${filter.field_name}_${value.code}` && (
                                <div
                                  className="uhc-filter-tooltip"
                                  style={tooltipStyles}
                                >
                                  {getFilterTooltip(
                                    filter.field_name,
                                    value.code,
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </fieldset>
              ) : (
                <div
                  key={filter.field_name}
                  className="tui__label_text tui__secondary_dropdown tui__chart_dropdown tui_input_select"
                >
                  <label id={filter.field_name}>
                    {filter.field_name?.startsWith("COMP_BREAKDOWN")
                      ? getCustomDimensionName(
                          filter.field_value,
                          filter.field_name,
                        )
                      : filter?.label_name?.trim()
                        ? filter.label_name
                        : ""}
                  </label>
                  <select
                    htmlFor={filter.field_name}
                    value={selectedFilters[filter.field_name]}
                    name={filter.field_name}
                    onChange={handleFilterChange}
                  >
                    {filter.field_value.map((item) => (
                      <option
                        key={`${filter.field_name}_${
                          item.code || item.description
                        }`}
                        value={item.code}
                        disabled={
                          dimensionsData &&
                          changedFilterName !== filter.field_name &&
                          !dimensionsData[filter.field_name]?.includes(
                            item.code,
                          )
                        }
                      >
                        {" "}
                        {filter.field_name?.startsWith("COMP_BREAKDOWN")
                          ? getCustomDimensionValue(item)
                          : item.description || item.code}
                      </option>
                    ))}
                  </select>
                  <span className="select-arrow"></span>
                </div>
              ),
            )}
          {chartType === chartTypes.DataTable &&
            comparisonByData.map((filter) => (
              <Fragment key={`fragment_${filter.field_name}`}>
                {!(
                  filter?.field_value?.length > 6 &&
                  filter.field_name?.startsWith("COMP_BREAKDOWN")
                ) ? (
                  <fieldset
                    key={filter.field_name}
                    className="primary_checkbox"
                    id={`compare_fieldset_${filter.field_name}`}
                  >
                    <legend id={filter.field_name}>
                      {filter.field_name?.startsWith("COMP_BREAKDOWN")
                        ? getCustomDimensionName(
                            filter.field_value,
                            filter.field_name,
                          )
                        : filter?.label_name?.trim()
                          ? filter.label_name
                          : ""}
                    </legend>
                    <div className="tui__checkbox_group">
                      {filter.field_value.map((value) => {
                        const filterKey = filter.field_name;
                        const pendingValue = currentRadioSelections[filterKey];
                        const selectedValue = selectedFilters[filterKey];
                        const activeValue =
                          pendingValue !== undefined
                            ? isDimensionsLoading
                              ? pendingValue
                              : (selectedValue ?? pendingValue)
                            : selectedValue;

                        return (
                          <div
                            key={`${filter.field_name}_${value.code}`}
                            className="tui__checkbox_inline"
                          >
                            <input
                              className="tui__primary_checkbox"
                              type="checkbox"
                              id={`${filter.field_name}_${value.code}`}
                              name={filterKey}
                              value={value.code}
                              checked={activeValue
                                ?.split(",")
                                .includes(value.code)}
                              onChange={(e) =>
                                handleCheckboxChange(e, filterKey)
                              }
                              disabled={
                                dimensionsData &&
                                changedFilterName !== filter.field_name &&
                                !dimensionsData[filter.field_name]?.includes(
                                  value.code,
                                )
                              }
                            />
                            <label
                              htmlFor={`${filter.field_name}_${value.code}`}
                            >
                              <span>
                                {filter.field_name?.startsWith("COMP_BREAKDOWN")
                                  ? getCustomDimensionValue(value)
                                  : value.description || value.code}
                              </span>
                            </label>
                            {getFilterTooltip(
                              filter.field_name,
                              value.code,
                            ) && (
                              <div
                                style={{
                                  position: "relative",
                                  display: "inline-block",
                                  marginLeft: "8px",
                                }}
                              >
                                <span
                                  className="tui__filter_info_icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveTooltip(
                                      activeTooltip ===
                                        `tooltip_${filter.field_name}_${value.code}`
                                        ? null
                                        : `tooltip_${filter.field_name}_${value.code}`,
                                    );
                                  }}
                                  style={{ cursor: "pointer" }}
                                >
                                  <i className="lp lp-info-circle-solid"></i>
                                </span>
                                {activeTooltip ===
                                  `tooltip_${filter.field_name}_${value.code}` && (
                                  <div
                                    className="uhc-filter-tooltip"
                                    style={tooltipStyles}
                                  >
                                    {getFilterTooltip(
                                      filter.field_name,
                                      value.code,
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </fieldset>
                ) : (
                  <fieldset
                    key={filter.field_name}
                    className="primary_checkbox checkbox-dropdown"
                    id={`compare_fieldset_${filter.field_name}`}
                  >
                    <legend id={filter.field_name}>
                      {filter.field_name?.startsWith("COMP_BREAKDOWN")
                        ? getCustomDimensionName(
                            filter.field_value,
                            filter.field_name,
                          )
                        : filter?.label_name?.trim()
                          ? filter.label_name
                          : ""}
                    </legend>
                    <div
                      ref={custDimMultiSelectListRef}
                      className="tui_selected_dropdown"
                    >
                      <div
                        className="tui_selected_value"
                        onClick={() => toggleCheckboxGroup(filter.field_name)}
                      >{`Select ${getCustomDimensionName(
                        filter.field_value,
                        filter.field_name,
                      )}`}</div>
                      {activeDropdown === filter.field_name && (
                        <div className="tui__checkbox_group">
                          {filter.field_value.map((value) => {
                            const filterKey = filter.field_name;
                            const pendingValue =
                              currentRadioSelections[filterKey];
                            const selectedValue = selectedFilters[filterKey];
                            const activeValue =
                              pendingValue !== undefined
                                ? isDimensionsLoading
                                  ? pendingValue
                                  : (selectedValue ?? pendingValue)
                                : selectedValue;

                            return (
                              <div
                                key={`${filter.field_name}_${value.code}`}
                                className="tui__checkbox"
                                data-multi-checkbox={true}
                              >
                                <input
                                  data-multi-checkbox={true}
                                  className="tui__primary_checkbox"
                                  type="checkbox"
                                  id={`${filter.field_name}_${value.code}`}
                                  name={filterKey}
                                  value={value.code}
                                  checked={activeValue
                                    ?.split(",")
                                    .includes(value.code)}
                                  onChange={(e) =>
                                    handleCheckboxChange(e, filterKey)
                                  }
                                  disabled={
                                    dimensionsData &&
                                    changedFilterName !== filter.field_name &&
                                    !dimensionsData[
                                      filter.field_name
                                    ]?.includes(value.code)
                                  }
                                />
                                <label
                                  data-multi-checkbox={true}
                                  htmlFor={`${filter.field_name}_${value.code}`}
                                >
                                  <span data-multi-checkbox={true}>
                                    {filter.field_name?.startsWith(
                                      "COMP_BREAKDOWN",
                                    )
                                      ? getCustomDimensionValue(value)
                                      : value.description || value.code}
                                  </span>
                                </label>
                                {getFilterTooltip(
                                  filter.field_name,
                                  value.code,
                                ) && (
                                  <div
                                    style={{
                                      position: "relative",
                                      display: "inline-block",
                                      marginLeft: "8px",
                                    }}
                                  >
                                    <span
                                      data-multi-checkbox={true}
                                      className="tui__filter_info_icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveTooltip(
                                          activeTooltip ===
                                            `tooltip_${filter.field_name}_${value.code}`
                                            ? null
                                            : `tooltip_${filter.field_name}_${value.code}`,
                                        );
                                      }}
                                      style={{ cursor: "pointer" }}
                                    >
                                      <i
                                        className="lp lp-info-circle-solid"
                                        data-multi-checkbox={true}
                                      ></i>
                                    </span>
                                    {activeTooltip ===
                                      `tooltip_${filter.field_name}_${value.code}` && (
                                      <div
                                        className="uhc-filter-tooltip"
                                        style={tooltipStyles}
                                      >
                                        {getFilterTooltip(
                                          filter.field_name,
                                          value.code,
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </fieldset>
                )}
              </Fragment>
            ))}

          {selectedComparison !== NONE &&
            chartType !== chartTypes.DataTable && (
              <>
                {!(
                  comparisonByData.find(
                    (filter) => filter.field_name === selectedComparison,
                  )?.field_value?.length > 6 &&
                  selectedComparison?.startsWith("COMP_BREAKDOWN")
                ) ? (
                  <fieldset
                    key={selectedComparison}
                    className="primary_radio"
                    id={`compare_fieldset_${selectedComparison}`}
                  >
                    <legend id={selectedComparison}>
                      {/* {formatLabel(selectedComparison)} */}
                      {selectedComparison?.startsWith("COMP_BREAKDOWN")
                        ? getCustomDimensionName(
                            comparisonByData.find(
                              (filter) =>
                                filter.field_name === selectedComparison,
                            )?.field_value,
                            selectedComparison,
                          )
                        : comparisonByData
                              .find(
                                (filter) =>
                                  filter.field_name === selectedComparison,
                              )
                              ?.label_name?.trim()
                          ? comparisonByData.find(
                              (filter) =>
                                filter.field_name === selectedComparison,
                            )?.label_name
                          : ""}
                    </legend>
                    <div className="tui__checkbox_group">
                      {comparisonByData
                        .find(
                          (filter) => filter.field_name === selectedComparison,
                        )
                        ?.field_value?.filter(
                          (value) =>
                            !(
                              (chartType === chartTypes.StackedBar ||
                                chartType === chartTypes.Pie) &&
                              selectedComparison !== NONE &&
                              value.code === TOTAL
                            ),
                        )
                        ?.map((value) => {
                          const pendingValue =
                            currentRadioSelections[selectedComparison];
                          const selectedValue =
                            selectedFilters[selectedComparison];
                          const activeValue =
                            pendingValue !== undefined
                              ? isDimensionsLoading
                                ? pendingValue
                                : (selectedValue ?? pendingValue)
                              : selectedValue;

                          return (
                            <div
                              key={`${selectedComparison}_${value.code}`}
                              className="tui__checkbox"
                            >
                              <input
                                className="tui__primary_checkbox"
                                type="checkbox"
                                id={`${selectedComparison}_${value.code}`}
                                name={selectedComparison}
                                value={value.code}
                                checked={activeValue
                                  ?.split(",")
                                  .includes(value.code)}
                                onChange={(e) =>
                                  handleCheckboxChange(e, selectedComparison)
                                }
                                disabled={
                                  dimensionsData &&
                                  changedFilterName !== selectedComparison &&
                                  !dimensionsData[selectedComparison]?.includes(
                                    value.code,
                                  )
                                }
                              />
                              <label
                                htmlFor={`${selectedComparison}_${value.code}`}
                              >
                                <span>
                                  {selectedComparison?.startsWith(
                                    "COMP_BREAKDOWN",
                                  )
                                    ? getCustomDimensionValue(value)
                                    : value.description || value.code}
                                </span>
                              </label>
                              {getFilterTooltip(
                                selectedComparison,
                                value.code,
                              ) && (
                                <div
                                  style={{
                                    position: "relative",
                                    display: "inline-block",
                                    marginLeft: "8px",
                                  }}
                                >
                                  <span
                                    className="tui__filter_info_icon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveTooltip(
                                        activeTooltip ===
                                          `tooltip_${selectedComparison}_${value.code}`
                                          ? null
                                          : `tooltip_${selectedComparison}_${value.code}`,
                                      );
                                    }}
                                    style={{ cursor: "pointer" }}
                                  >
                                    <i className="lp lp-info-circle-solid"></i>
                                  </span>
                                  {activeTooltip ===
                                    `tooltip_${selectedComparison}_${value.code}` && (
                                    <div
                                      className="uhc-filter-tooltip"
                                      style={tooltipStyles}
                                    >
                                      {getFilterTooltip(
                                        selectedComparison,
                                        value.code,
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </fieldset>
                ) : (
                  <fieldset
                    key={selectedComparison}
                    className="primary_radio checkbox-dropdown"
                    id={`compare_fieldset_${selectedComparison}`}
                  >
                    <legend id={selectedComparison}>
                      {/* {formatLabel(selectedComparison)} */}
                      {selectedComparison?.startsWith("COMP_BREAKDOWN")
                        ? getCustomDimensionName(
                            comparisonByData.find(
                              (filter) =>
                                filter.field_name === selectedComparison,
                            )?.field_value,
                            selectedComparison,
                          )
                        : comparisonByData
                              .find(
                                (filter) =>
                                  filter.field_name === selectedComparison,
                              )
                              ?.label_name?.trim()
                          ? comparisonByData.find(
                              (filter) =>
                                filter.field_name === selectedComparison,
                            )?.label_name
                          : ""}
                    </legend>

                    <div
                      ref={custDimMultiSelectListRef}
                      className="tui_selected_dropdown"
                    >
                      <div
                        className="tui_selected_value"
                        onClick={() => toggleCheckboxGroup(selectedComparison)}
                      >{`Select ${getCustomDimensionName(
                        comparisonByData.find(
                          (filter) => filter.field_name === selectedComparison,
                        )?.field_value,
                        selectedComparison,
                      )}`}</div>
                      {activeDropdown === selectedComparison && (
                        <div className="tui__checkbox_group">
                          <div
                            className="tui__checkbox"
                            key={`${selectedComparison}_SelectAll`}
                            data-multi-checkbox={true}
                          >
                            <input
                              data-multi-checkbox={true}
                              className="tui__primary_checkbox"
                              type="checkbox"
                              id={`${selectedComparison}_selectAll`}
                              name={selectedComparison}
                              value={"selectAll"}
                              checked={selectAllChecked}
                              onChange={(e) =>
                                handleSelectAllChange(e, selectedComparison)
                              }
                            />
                            <label htmlFor={`selectAll_${selectedComparison}`}>
                              <span>Select All</span>
                            </label>
                          </div>
                          {dimensionValues.map((value) => {
                            const pendingValue =
                              currentRadioSelections[selectedComparison];
                            const selectedValue =
                              selectedFilters[selectedComparison];
                            const activeValue =
                              pendingValue !== undefined
                                ? isDimensionsLoading
                                  ? pendingValue
                                  : (selectedValue ?? pendingValue)
                                : selectedValue;

                            return (
                              <div
                                key={`${selectedComparison}_${value.code}`}
                                className="tui__checkbox"
                                data-multi-checkbox={true}
                              >
                                <input
                                  data-multi-checkbox={true}
                                  className="tui__primary_checkbox"
                                  type="checkbox"
                                  id={`${selectedComparison}_${value.code}`}
                                  name={selectedComparison}
                                  value={value.code}
                                  checked={activeValue
                                    ?.split(",")
                                    .includes(value.code)}
                                  onChange={(e) =>
                                    handleDropdownCheckboxChange(
                                      e,
                                      selectedComparison,
                                    )
                                  }
                                  disabled={
                                    (dimensionsData &&
                                      changedFilterName !==
                                        selectedComparison &&
                                      !dimensionsData[
                                        selectedComparison
                                      ]?.includes(value.code)) ||
                                    (chartType !== chartTypes.Trend &&
                                      activeValue?.split(",").length >= 6 &&
                                      !activeValue
                                        ?.split(",")
                                        .includes(value.code))
                                  }
                                />
                                <label
                                  htmlFor={`${selectedComparison}_${value.code}`}
                                  data-multi-checkbox={true}
                                >
                                  <span data-multi-checkbox={true}>
                                    {selectedComparison?.startsWith(
                                      "COMP_BREAKDOWN",
                                    )
                                      ? getCustomDimensionValue(value)
                                      : value.description || value.code}
                                  </span>
                                </label>
                                {getFilterTooltip(
                                  selectedComparison,
                                  value.code,
                                ) && (
                                  <div
                                    style={{
                                      position: "relative",
                                      display: "inline-block",
                                      marginLeft: "8px",
                                    }}
                                  >
                                    <span
                                      data-multi-checkbox={true}
                                      className="tui__filter_info_icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveTooltip(
                                          activeTooltip ===
                                            `tooltip_${selectedComparison}_${value.code}`
                                            ? null
                                            : `tooltip_${selectedComparison}_${value.code}`,
                                        );
                                      }}
                                      style={{ cursor: "pointer" }}
                                    >
                                      <i
                                        className="lp lp-info-circle-solid"
                                        data-multi-checkbox={true}
                                      ></i>
                                    </span>
                                    {activeTooltip ===
                                      `tooltip_${selectedComparison}_${value.code}` && (
                                      <div
                                        className="uhc-filter-tooltip"
                                        style={tooltipStyles}
                                      >
                                        {getFilterTooltip(
                                          selectedComparison,
                                          value.code,
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <p className="tui_text_sm" style={{ marginTop: "10px" }}>
                      {`You can select a maximum of 6 ${getCustomDimensionName(
                        comparisonByData.find(
                          (filter) => filter.field_name === selectedComparison,
                        )?.field_value,
                        selectedComparison,
                      )}`}
                    </p>
                  </fieldset>
                )}

                {comparisonByData
                  .filter((item) => item.field_name !== selectedComparison)
                  .map((filter) => (
                    <fieldset
                      key={filter.field_name}
                      className="primary_radio"
                      id={`compare_fieldset_${filter.field_name}`}
                    >
                      <legend id={filter.field_name}>
                        {filter.field_name?.startsWith("COMP_BREAKDOWN")
                          ? getCustomDimensionName(
                              filter.field_value,
                              filter.field_name,
                            )
                          : filter?.label_name?.trim()
                            ? filter.label_name
                            : ""}
                      </legend>
                      <div className="tui__radio_group">
                        {filter.field_value.map((value) => {
                          const pendingValue =
                            currentRadioSelections[filter.field_name];
                          const selectedValue =
                            selectedFilters[filter.field_name];
                          const activeValue =
                            pendingValue !== undefined
                              ? isDimensionsLoading
                                ? pendingValue
                                : (selectedValue ?? pendingValue)
                              : selectedValue;

                          return (
                            <div
                              key={`${filter.field_name}_${value.code}`}
                              className="tui__radio"
                            >
                              <input
                                className="tui__primary_radio"
                                type="radio"
                                id={`${filter.field_name}_${value.code}`}
                                name={filter.field_name}
                                value={value.code}
                                checked={activeValue === value.code}
                                onChange={handleFilterChange}
                                disabled={
                                  dimensionsData &&
                                  changedFilterName !== filter.field_name &&
                                  !dimensionsData[filter.field_name]?.includes(
                                    value.code,
                                  )
                                }
                              />
                              <label
                                htmlFor={`${filter.field_name}_${value.code}`}
                              >
                                <span>
                                  {filter.field_name?.startsWith(
                                    "COMP_BREAKDOWN",
                                  )
                                    ? getCustomDimensionValue(value)
                                    : value.description || value.code}
                                </span>
                              </label>
                              {getFilterTooltip(
                                filter.field_name,
                                value.code,
                              ) && (
                                <div
                                  style={{
                                    position: "relative",
                                    display: "inline-block",
                                    marginLeft: "8px",
                                  }}
                                >
                                  <span
                                    className="tui__filter_info_icon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveTooltip(
                                        activeTooltip ===
                                          `tooltip_${filter.field_name}_${value.code}`
                                          ? null
                                          : `tooltip_${filter.field_name}_${value.code}`,
                                      );
                                    }}
                                    style={{ cursor: "pointer" }}
                                  >
                                    <i className="lp lp-info-circle-solid"></i>
                                  </span>
                                  {activeTooltip ===
                                    `tooltip_${filter.field_name}_${value.code}` && (
                                    <div
                                      className="uhc-filter-tooltip"
                                      style={tooltipStyles}
                                    >
                                      {getFilterTooltip(
                                        filter.field_name,
                                        value.code,
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </fieldset>
                  ))}
              </>
            )}
        </div>
      )}
    </>
  );
};
export default ComparisonByDisaggregation;
